// src/app/appHelpers.ts
/**
 * Pure helpers used by the authenticated app shell.
 *
 * Extracted from AuthedApp.tsx (plans/monolith-migration-plan.md §11.12, Batch 9).
 * Both are security-relevant, which is exactly why they belong in a tested module
 * rather than inline in a 1,277-line component:
 *  - getRoleData avoids dynamic bracket-index lookup (CWE-94).
 *  - getSafeIframeUrl allow-lists the https: protocol before rendering a tenant URL.
 */
import { ROLES } from '../constants';

/**
 * Look up a role's configuration by key without dynamic property access.
 * Returns null for an unknown or missing key.
 */
export function getRoleData(roleKey: string | null | undefined): (typeof ROLES)[keyof typeof ROLES] | null {
    const entry = Object.entries(ROLES || {}).find(([k]) => k === roleKey);
    return entry ? (entry[1] as (typeof ROLES)[keyof typeof ROLES]) : null;
}

/** Fallback shown when a tenant has no custom iframe configured. */
export const DEFAULT_IFRAME_URL = 'https://app.example.com/';

/**
 * Return a tenant-configured iframe URL only if it parses and uses https:.
 * Anything else (http:, javascript:, data:, malformed) becomes 'about:blank'.
 */
export function getSafeIframeUrl(url: string | null | undefined): string {
    if (!url) return DEFAULT_IFRAME_URL;
    try {
        const parsed = new URL(url);
        if (parsed.protocol === 'https:') {
            return parsed.href;
        }
    } catch {
        // Ignore invalid url format
    }
    return 'about:blank';
}
